import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { UnknownAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import type { Message } from '@ag-ui/client';
import { SIGN_OUT_CODES } from '../../constants/auth.constants';
import {
  setAIV2ConversationId,
  clearAIV2Conversation,
  clearAIV2Handoff,
} from '../../store/actions/ai-v2.actions';
import { logout } from '../../store/actions/auth.actions';
import { isAIEnabled, isPandaV2Enabled } from '../../store/selectors/features.selector';

interface AiV2Slice {
  conversationId?: string;
  isChatOpen: boolean;
  messages: Message[];
  handoffId?: string;
}

// Minimal store shape the hook reads; both hosts' stores satisfy it. The
// feature selectors are untyped JS destructuring `{ organization, auth }`, so
// those slices must be declared for the weak-type check even though only
// `aiV2` is read directly.
interface PandaChatStoreState {
  aiV2: AiV2Slice;
  organization?: object;
  auth?: object;
}

export interface PandaChatStore {
  conversationId: string | null;
  isOpen: boolean;
  isChatEnabled: boolean;
  handoffId?: string;
  setConversationId: (conversationId: string) => void;
  clearConversation: () => void;
  clearHandoff: () => void;
  /** Ends the session when auth is unrecoverable (refresh token rejected). */
  logout: () => void;
}

/**
 * Everything the shared conversation provider needs from the store, behind one
 * interface — moving the feature off Redux later means reimplementing this hook
 * (and the sibling panda-chat-persistence.hooks).
 */
export function usePandaChatStore(): PandaChatStore {
  // Thunk-aware so the `logout` thunk dispatches without a host-typed dispatch.
  const dispatch = useDispatch<ThunkDispatch<PandaChatStoreState, undefined, UnknownAction>>();

  // JS reducer types conversationId as literal `undefined`; annotate `string | null`.
  const conversationId = useSelector(
    (state: PandaChatStoreState): string | null => state.aiV2.conversationId ?? null,
  );
  const isOpen = useSelector((state: PandaChatStoreState): boolean => state.aiV2.isChatOpen);
  const handoffId = useSelector(
    (state: PandaChatStoreState): string | undefined => state.aiV2.handoffId,
  );
  // V2 is reachable only when AI is on AND the panda-v2 flag is on.
  const isChatEnabled = useSelector(
    (state: PandaChatStoreState): boolean => isAIEnabled(state) && isPandaV2Enabled(state),
  );

  const setConversationId = useCallback(
    (id: string) => dispatch(setAIV2ConversationId(id)),
    [dispatch],
  );
  const clearConversation = useCallback(() => dispatch(clearAIV2Conversation()), [dispatch]);
  const clearHandoff = useCallback(() => dispatch(clearAIV2Handoff()), [dispatch]);
  const logoutSession = useCallback(
    () => dispatch(logout({ skipApiCall: true, signOutCode: SIGN_OUT_CODES.TOKEN_REFRESH_FAILED })),
    [dispatch],
  );

  return {
    conversationId,
    isOpen,
    isChatEnabled,
    handoffId,
    setConversationId,
    clearConversation,
    clearHandoff,
    logout: logoutSession,
  };
}
